/**
 * useProjectCanonicalNetworkDisplay
 *
 * Carga la red urbana canónica real del proyecto desde /network/edges.
 * Solo lectura — sin stores de edición, sin nodos, sin feature states.
 *
 * Bloque B: agrega edgeId, scope (inside/outside) calculado por bbox del proyecto.
 *   - edgeId duplicado en properties para joins futuros por indicadores.
 *   - scope: punto medio entre primera y última coord. del LineString vs. bounds.
 *   - Sin Turf, sin dependencias externas.
 */
import type { FeatureCollection, LineString } from 'geojson';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../auth';

const API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:8080/api/v1';

// Interfaz mínima — no importa tipos del módulo spatial/
interface CanonicalEdge {
  id: string;
  geometry: LineString;
  roadClass: string | null;
  status: string;
}

/** Bounding box del proyecto — para calcular scope de cada edge. */
interface Bounds {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

export interface UseProjectCanonicalNetworkDisplayResult {
  featureCollection: FeatureCollection | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Clasifica un edge como 'inside' o 'outside' del área de proyecto.
 * Usa el punto medio entre la primera y última coordenada del LineString.
 * Si no hay bounds, todo es 'inside' para no atenuar toda la red.
 */
function calcScope(geometry: LineString, bounds: Bounds | undefined): 'inside' | 'outside' {
  if (!bounds || geometry.coordinates.length === 0) return 'inside';
  const first = geometry.coordinates[0];
  const last = geometry.coordinates[geometry.coordinates.length - 1];
  const midLng = (first[0] + last[0]) / 2;
  const midLat = (first[1] + last[1]) / 2;
  const inBounds =
    midLng >= bounds.minLng &&
    midLng <= bounds.maxLng &&
    midLat >= bounds.minLat &&
    midLat <= bounds.maxLat;
  return inBounds ? 'inside' : 'outside';
}

function buildFeatureCollection(edges: CanonicalEdge[], bounds?: Bounds): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: edges
      .filter(e => e.status === 'active')
      .map(e => ({
        type: 'Feature' as const,
        id: e.id,
        geometry: e.geometry,
        properties: {
          roadClass: e.roadClass,
          edgeId: e.id,
          scope: calcScope(e.geometry, bounds),
        },
      })),
  };
}

export function useProjectCanonicalNetworkDisplay(
  projectId: string | null,
  projectBounds?: Bounds | null
): UseProjectCanonicalNetworkDisplayResult {
  const { token } = useAuthStore();
  const [featureCollection, setFeatureCollection] = useState<FeatureCollection | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Primitivos estables para evitar re-renders por referencia de objeto nuevo
  const minLng = projectBounds?.minLng;
  const minLat = projectBounds?.minLat;
  const maxLng = projectBounds?.maxLng;
  const maxLat = projectBounds?.maxLat;

  useEffect(() => {
    if (!projectId || !token) return;

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    const bounds: Bounds | undefined =
      minLng !== undefined && minLat !== undefined && maxLng !== undefined && maxLat !== undefined
        ? { minLng, minLat, maxLng, maxLat }
        : undefined;

    fetch(`${API_URL}/projects/${projectId}/network/edges/display`, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`Canonical edges fetch failed: ${r.status}`);
        return r.json() as Promise<CanonicalEdge[]>;
      })
      .then(edges => {
        setFeatureCollection(buildFeatureCollection(edges, bounds));
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(err);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });

    return (): void => {
      controller.abort();
    };
  }, [projectId, token, minLng, minLat, maxLng, maxLat]);

  return { featureCollection, isLoading, error };
}
